import React from "react";
import { MapView, type MapViewProps } from "../../MapView";
import {
  CONSTRUCTION_DURATION,
  CONSTRUCTION_LIFT,
  constructionTiming
} from "../map/animation/constructionTiming";
import { useArrivalAnimation } from "./useArrivalAnimation";

/** Presentation only: never writes animated positions into map data. */
export function MapConstructionPreview({
  replay,
  arrivals,
  ...props
}: MapViewProps & {
  replay: number;
  /** Shapes already shown; when given, only newly arrived shapes animate in. */
  arrivals?: React.RefObject<Set<string>>;
}) {
  const ref = React.useRef<SVGSVGElement | null>(null);
  useArrivalAnimation(ref, props.value, replay ? undefined : arrivals);
  React.useLayoutEffect(() => {
    const svg = ref.current;
    if (
      !svg ||
      !props.value ||
      !replay ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    )
      return;
    const originalVisibility = svg.style.visibility;
    const animations: Animation[] = [];
    const start = () => {
      const nodes = Array.from(
        svg.querySelectorAll<SVGGElement>("[data-construction-shape]")
      ).filter((node) => node.dataset.constructionLayer !== "background");
      if (!nodes.length) return false;
      const rank = (node: SVGGElement) =>
        node.dataset.constructionShape === "wall-path" ||
        node.dataset.constructionShape === "wall"
          ? 0
          : node.dataset.constructionShape === "text" ||
              node.dataset.constructionShape === "spot"
            ? 2
            : 1;
      const positions = new Map(nodes.map((node) => [node, node.getBBox().y]));
      nodes.sort(
        (a, b) => rank(a) - rank(b) || positions.get(a)! - positions.get(b)!
      );
      const timing = constructionTiming(nodes.length);
      nodes.forEach((node, index) => {
        if (!node.animate) return;
        const distance = CONSTRUCTION_LIFT;
        animations.push(
          node.animate(
            [
              { transform: `translateY(${distance}px)`, opacity: 0 },
              { transform: "translateY(0px)", opacity: 1 }
            ],
            {
              duration: CONSTRUCTION_DURATION,
              delay: timing.delay(index),
              easing: "cubic-bezier(0.16, 1, 0.3, 1)",
              fill: "backwards"
            }
          )
        );
      });
      // These derived decorations are not independent map objects.
      svg
        .querySelectorAll<SVGGElement>("[data-construction-decoration]")
        .forEach((node) => {
          if (node.animate)
            animations.push(
              node.animate([{ opacity: 0 }, { opacity: 1 }], {
                duration: 300,
                delay: timing.end,
                fill: "backwards"
              })
            );
        });
      svg.style.visibility = originalVisibility;
      return true;
    };
    // MapCanvasProvider hydrates shapes in an effect after this SVG mounts.
    const observer = new MutationObserver(() => {
      if (start()) observer.disconnect();
    });
    if (
      !start() &&
      props.value.shapes.some(
        (shape) => shape.layerType !== "background" && !shape.hidden
      )
    ) {
      svg.style.visibility = "hidden";
      observer.observe(svg, { childList: true, subtree: true });
    }
    // Hidden/category-filtered shapes may never create a renderable node.
    const revealTimer = window.setTimeout(() => {
      svg.style.visibility = originalVisibility;
    }, 1000);
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const stop = () => {
      observer.disconnect();
      window.clearTimeout(revealTimer);
      svg.style.visibility = originalVisibility;
      animations.forEach((animation) => {
        animation.cancel();
      });
    };
    preference.addEventListener("change", stop);
    return () => {
      stop();
      preference.removeEventListener("change", stop);
    };
  }, [replay, props.value]);
  return <MapView {...props} ref={ref} useSVG />;
}
